import type { CSSProperties } from 'react';
import { color, chaseTheme, redcapRuleTheme } from '../styles/theme';
import { num, plainDate, shortDate } from '../lib/format';
import { useAuth } from '../auth/AuthContext';
import {
  useDictionary,
  useExportMap,
  useExportPlan,
  useRedcapBoard,
  useRedcapSetup,
  useRedcapWeeks,
  type ExportFilterInput,
} from '../api/redcap';
import { IconCheck } from '../components/icons';
import { Card } from '../components/ui';

/**
 * REDCap, step by step.
 *
 * The five tabs are one procedure, and this is where it is spelled out: what
 * each step is for, how often it comes round, and — read live from the same
 * queries the tabs use — where this study stands on it. The rail is the tab
 * list, so the order a newcomer reads is the order the work is done in, and a
 * returning coordinator sees what is late before opening anything.
 *
 * Why these five, in this order: step 1 is the setup everything else reads
 * (the project, the lab's dictionary, the forms kept). Steps 2–4 are the
 * three files moved by hand between REDCap and this dashboard — the Monday
 * completeness counts coming in, accepted sign-ups going out, de-identified
 * answers coming back — each independent of the others, none possible before
 * step 1. Step 5 only counts what step 4 brought in. The intake workflow
 * (v1.0) is why every one is a CSV carried by a person: REDCap is the only
 * system permitted to hold clinical content, and there is no live link.
 */

export type RedcapTab = 'setup' | 'completeness' | 'export' | 'records' | 'analytics';

export interface StepMeta {
  id: RedcapTab;
  label: string;
  /** How often this step comes round. */
  cadence: string;
}

export const STEPS: ReadonlyArray<StepMeta> = [
  { id: 'setup', label: 'Project setup', cadence: 'Once per study' },
  { id: 'completeness', label: 'This week', cadence: 'Every Monday' },
  { id: 'export', label: 'Export to REDCap', cadence: 'When sign-ups are accepted' },
  { id: 'records', label: 'Record import', cadence: 'After a REDCap export' },
  { id: 'analytics', label: 'Analytics', cadence: 'Any time' },
];

export const stepNumber = (id: RedcapTab): number => STEPS.findIndex((s) => s.id === id) + 1;

/**
 * done — nothing to do here now · todo — there is work, or it has not
 * started · attention — something is late or broken · waiting — an earlier
 * step has to settle first.
 */
export type StepState = 'done' | 'todo' | 'attention' | 'waiting';

export interface StepStatus {
  state: StepState;
  /** One line under the step: where this study stands. */
  line: string;
}

export interface RedcapStatus {
  /** False while the queries behind the lines are still loading. */
  ready: boolean;
  steps: Record<RedcapTab, StepStatus>;
  /** Step 1 complete: project linked, dictionary on file, forms kept chosen. */
  setupDone: boolean;
}

/** The export tab's opening filter — the same query key, so the plan is fetched once for the rail and the tab. */
export const DEFAULT_EXPORT_FILTER: ExportFilterInput = { form: 'intake', since: null, status: null, onlyNew: true };

const STATE_WORD: Record<StepState, string> = {
  done: 'Done',
  todo: 'To do',
  attention: 'Needs attention',
  waiting: 'Waiting',
};

const waitingOn = (step: RedcapTab): StepStatus => ({ state: 'waiting', line: `After step ${stepNumber(step)}` });
const plural = (n: number, one: string, many = `${one}s`): string => `${num(n)} ${n === 1 ? one : many}`;
const settled = (q: { isSuccess: boolean; isError: boolean }): boolean => q.isSuccess || q.isError;

/**
 * Where one study stands on each step. Every line is derived from data the
 * tabs already fetch, so the rail costs no request of its own once a tab has
 * loaded, and never disagrees with what the tab shows.
 */
export function useRedcapStatus(slug: string | null): RedcapStatus {
  const { can } = useAuth();
  const canBoard = can('submissions:read');
  const canPlan = can('submissions:export') && can('submissions:read_phi');
  const dict = useDictionary(slug);
  const setup = useRedcapSetup();
  const map = useExportMap();
  const weeks = useRedcapWeeks();
  const board = useRedcapBoard(slug, canBoard);
  const plan = useExportPlan(slug, DEFAULT_EXPORT_FILTER, canPlan);

  const ready =
    slug !== null &&
    settled(dict) &&
    settled(setup) &&
    settled(map) &&
    settled(weeks) &&
    (!canBoard || settled(board)) &&
    (!canPlan || settled(plan));

  if (!ready) {
    const checking: StepStatus = { state: 'todo', line: 'Checking…' };
    return {
      ready: false,
      setupDone: false,
      steps: { setup: checking, completeness: checking, export: checking, records: checking, analytics: checking },
    };
  }

  // The API itself failing (a 500, a study outside this person's scope) is
  // not a step waiting on another: say so on every step, with what it said.
  const failed = dict.error ?? setup.error ?? weeks.error ?? null;
  if (failed) {
    const line = (failed as Error).message || 'Could not load this study';
    const down: StepStatus = { state: 'todo', line };
    return { ready: true, setupDone: false, steps: { setup: down, completeness: down, export: down, records: down, analytics: down } };
  }

  const project = dict.data?.project ?? null;
  const dictFields = setup.data?.summary.fields ?? 0;
  const kept = setup.data?.setup.keptForms ?? null;
  const setupDone = project !== null && dictFields > 0 && kept !== null;
  const steps = {} as Record<RedcapTab, StepStatus>;

  // 1 · Project setup: the study's project, then the lab's dictionary and forms kept.
  if (!project) steps.setup = { state: 'todo', line: 'Not linked to a REDCap project yet' };
  else if (dictFields === 0) steps.setup = { state: 'todo', line: `PID ${project.pid} · dictionary not uploaded` };
  else if (kept === null) steps.setup = { state: 'todo', line: `PID ${project.pid} · forms to keep not chosen` };
  else {
    steps.setup = {
      state: 'done',
      line: `PID ${project.pid}${project.eventName ? ` · ${project.eventName}` : ''} · ${plural(kept.length, 'form')} kept`,
    };
  }

  // 2 · This week: late when something urgent is on the page or this Monday's file has not come.
  const w = weeks.data?.studies.find((x) => x.studySlug === slug);
  if (!project || !w) steps.completeness = waitingOn('setup');
  else if (!w.lastAsOf) steps.completeness = { state: 'todo', line: 'No week uploaded yet' };
  else {
    const week = `Week of ${plainDate(w.lastAsOf)}`;
    const age = w.uploadAgeDays ?? 0;
    if (w.late.urgent > 0) steps.completeness = { state: 'attention', line: `${plural(w.late.urgent, 'urgent item')} to talk about · ${week}` };
    else if (!w.isCurrentWeek && age >= 8) steps.completeness = { state: 'attention', line: `${week} · ${age} days old, this Monday’s file is missing` };
    else if (w.late.watch > 0) steps.completeness = { state: 'todo', line: `${plural(w.late.watch, 'item')} to watch · ${week}` };
    else steps.completeness = { state: 'done', line: `${week} · nothing to talk about` };
  }

  // 3 · Export to REDCap: the project (for the event name) and the dictionary
  // (for the variables) come first; then the map has to exist and be sound.
  const sent = board.data?.exported.length ?? 0;
  const p = plan.data?.plan;
  if (!project || dictFields === 0) steps.export = waitingOn('setup');
  else if ((map.data?.rows.length ?? 0) === 0) steps.export = { state: 'todo', line: 'Field map not set yet' };
  else if ((map.data?.problems.length ?? 0) > 0) steps.export = { state: 'attention', line: 'Field map needs fixing' };
  else if (p && p.count > 0) steps.export = { state: 'todo', line: `${plural(p.count, 'sign-up')} not sent yet` };
  else if (sent > 0) {
    const last = plan.data?.history[0];
    steps.export = { state: 'done', line: `${plural(sent, 'sign-up')} sent${last ? ` · last file ${shortDate(last.createdAt)}` : ''}` };
  } else steps.export = { state: 'done', line: 'Ready · nothing waiting to go' };

  // 4 · Record import: refused by the API until the dictionary is on file and
  // the forms are chosen, and read through the project's own record ID field.
  const records = board.data?.project?.records ?? 0;
  if (!project || dictFields === 0 || kept === null) steps.records = waitingOn('setup');
  else if (!canBoard) steps.records = { state: 'todo', line: 'Needs the permission to read submissions' };
  else if (records === 0) steps.records = { state: 'todo', line: 'No records imported yet' };
  else steps.records = { state: 'done', line: `${plural(records, 'record')} imported` };

  // 5 · Analytics counts what step 4 brought in.
  if (records === 0) steps.analytics = waitingOn('records');
  else steps.analytics = { state: 'done', line: `Counts over ${plural(records, 'record')}` };

  return { ready: true, steps, setupDone };
}

/** Where to land: setup until it is complete, then whatever is late, then the Monday ritual. */
export function suggestedTab(status: RedcapStatus): RedcapTab {
  if (!status.setupDone) return 'setup';
  const late = STEPS.find((s) => status.steps[s.id].state === 'attention');
  return late?.id ?? 'completeness';
}

function circleStyle(state: StepState, on: boolean): CSSProperties {
  const base: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    borderRadius: '50%',
    fontSize: 12,
    fontWeight: 800,
    flex: 'none',
    boxSizing: 'border-box',
    marginTop: 1,
  };
  // The done green and the chase red are the palette's own pairs, so a step
  // reads with the same weight a cleared chase or an overdue one has below.
  if (state === 'done') return { ...base, background: chaseTheme.done.bg, color: chaseTheme.done.fg };
  if (state === 'attention') return { ...base, background: redcapRuleTheme.chase.bg, color: redcapRuleTheme.chase.fg };
  return {
    ...base,
    background: color.white,
    color: state === 'waiting' ? color.muted : color.blueDeep,
    border: `1.5px solid ${on ? color.blue : color.line}`,
  };
}

/**
 * The five steps as the tab list: number, name, cadence, and this study's
 * line. The ones with nothing left to do are ticked, the late one is marked,
 * the ones an earlier step is holding up are greyed — so the screen says
 * what to do next without a paragraph.
 */
export function StepRail({
  status,
  tab,
  onPick,
}: {
  status: RedcapStatus;
  tab: RedcapTab | null;
  onPick: (tab: RedcapTab) => void;
}) {
  return (
    <Card pad="8px 10px">
      <div
        role="tablist"
        aria-label="REDCap, step by step"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(186px, 1fr))', gap: 4 }}
      >
        {STEPS.map((s, i) => {
          const st = status.steps[s.id];
          const on = tab === s.id;
          return (
            <button
              key={s.id}
              role="tab"
              aria-selected={on}
              aria-label={`Step ${i + 1}, ${s.label}. ${s.cadence}. ${STATE_WORD[st.state]}: ${st.line}`}
              onClick={() => onPick(s.id)}
              className={on ? undefined : 'hov-tint'}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                textAlign: 'left',
                padding: '10px 12px',
                borderRadius: 14,
                border: `1px solid ${on ? color.blue : 'transparent'}`,
                background: on ? color.tint : 'transparent',
                cursor: 'pointer',
                font: 'inherit',
                minWidth: 0,
              }}
            >
              <span aria-hidden="true" style={circleStyle(st.state, on)}>
                {st.state === 'done' ? <IconCheck size={13} /> : st.state === 'attention' ? '!' : i + 1}
              </span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 800,
                    lineHeight: '17px',
                    color: on ? color.blueDeep : st.state === 'waiting' ? color.muted : color.navy,
                  }}
                >
                  {s.label}
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: color.muted,
                    marginTop: 3,
                  }}
                >
                  {s.cadence}
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: 12,
                    lineHeight: '16px',
                    marginTop: 4,
                    color: st.state === 'attention' ? redcapRuleTheme.chase.fg : color.muted,
                    fontWeight: st.state === 'attention' ? 700 : 500,
                  }}
                >
                  {st.line}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}
